import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

interface OnboardingCompleteCelebrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display name for the company — used in the headline accent. */
  companyName: string;
  /** Triggered when the user clicks the primary CTA — closes the modal and
   *  navigates to the new-invoice screen. */
  onCreateFirstInvoice: () => void;
}

/**
 * Editorial celebration shown the first time a tenant lands on /dashboard
 * with the full DGI auto-flow + certificate done. Promotes the environment
 * flip from test (ePrueba) to prod and frames it as a milestone.
 */
export function OnboardingCompleteCelebrationModal({
  open,
  onOpenChange,
  companyName,
  onCreateFirstInvoice,
}: OnboardingCompleteCelebrationModalProps): JSX.Element {
  const { t } = useTranslation();

  const checklist = [
    t(
      "dashboard.onboardingComplete.point1",
      "Usuario e-Factura activo en DGI",
    ),
    t(
      "dashboard.onboardingComplete.point2",
      "CAEs descargados y listos para emitir",
    ),
    t(
      "dashboard.onboardingComplete.point3",
      "Fiscalo declarado como proveedor de software",
    ),
    t(
      "dashboard.onboardingComplete.point4",
      "Certificado digital cargado y validado",
    ),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg overflow-hidden border-0 p-0"
        style={{
          background: "var(--fx-s-inverse-bg)",
          color: "var(--fx-s-inverse-fg)",
          borderRadius: "var(--fx-r-lg)",
        }}
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={t("common.close", "Cerrar")}
          className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full text-[var(--fx-paper)]/60 transition hover:bg-white/10 hover:text-[var(--fx-paper)]"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Hero strip — sage glow + celebration badge */}
        <div
          className="relative flex flex-col items-center px-8 pt-10 pb-2 text-center"
          style={{
            background:
              "radial-gradient(120% 70% at 50% 0%, color-mix(in srgb, var(--fx-accent-300) 22%, transparent) 0%, transparent 65%)",
          }}
        >
          <div
            className="grid h-16 w-16 place-items-center rounded-full"
            style={{
              background: "var(--fx-accent-300)",
              color: "var(--fx-s-text)",
              boxShadow:
                "0 0 0 8px color-mix(in srgb, var(--fx-accent-300) 18%, transparent)",
            }}
            aria-hidden
          >
            <Check className="h-7 w-7" strokeWidth={2.5} />
          </div>
          <span
            className="mt-5 text-[11px] uppercase tracking-[0.22em] text-[var(--fx-paper)]/65"
            style={{ fontFamily: "var(--fx-ff-mono)" }}
          >
            {t(
              "dashboard.onboardingComplete.eyebrow",
              "Tu cuenta pasó a producción",
            )}
          </span>
          <DialogTitle asChild>
            <h2
              className="mt-3 text-[28px] leading-tight"
              style={{
                fontFamily: "var(--fx-ff-serif)",
                fontWeight: 500,
              }}
            >
              {companyName}{" "}
              <span
                className="italic"
                style={{ color: "var(--fx-accent-300)" }}
              >
                {t(
                  "dashboard.onboardingComplete.h2Accent",
                  "ya puede facturar en real.",
                )}
              </span>
            </h2>
          </DialogTitle>
        </div>

        <div className="px-8 pt-4 pb-8">
          <p className="text-sm leading-relaxed text-[var(--fx-paper)]/75">
            {t(
              "dashboard.onboardingComplete.body",
              "Terminamos la configuración con DGI y activamos el ambiente de producción. Las facturas que emitas desde ahora son comprobantes fiscales reales — ya no estás en ePrueba.",
            )}
          </p>

          {/* Checklist — sage check chips per item */}
          <ul
            className="mt-6 space-y-3 rounded-2xl border border-white/8 p-4"
            style={{
              background: "color-mix(in srgb, var(--fx-paper) 4%, transparent)",
            }}
          >
            {checklist.map((line) => (
              <li
                key={line}
                className="flex items-start gap-3 text-sm text-[var(--fx-paper)]/90"
              >
                <span
                  aria-hidden
                  className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
                  style={{
                    background:
                      "color-mix(in srgb, var(--fx-accent-300) 22%, transparent)",
                    color: "var(--fx-accent-300)",
                  }}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>

          {/* Actions: primary full-width, dismiss as subtle link below */}
          <div className="mt-7 flex flex-col items-stretch gap-3">
            <button
              type="button"
              onClick={onCreateFirstInvoice}
              className="rounded-full px-5 py-3 text-sm font-medium transition"
              style={{
                background: "var(--fx-accent-300)",
                color: "var(--fx-s-text)",
                fontFamily: "var(--fx-ff-sans)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--fx-accent-500)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--fx-accent-300)";
              }}
            >
              {t(
                "dashboard.onboardingComplete.primary",
                "Crear mi primera factura real",
              )}
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-center text-xs text-[var(--fx-paper)]/55 underline-offset-4 transition hover:text-[var(--fx-paper)]/85 hover:underline"
              style={{ fontFamily: "var(--fx-ff-sans)" }}
            >
              {t("dashboard.onboardingComplete.dismiss", "Ahora no, gracias")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
